import React, { useState, useEffect, useRef } from 'react';

import { Box } from '@wds/box';
import { IconSearch, IconX } from '@wds/icons';
import { Input } from '@wds/input';
import { InputGroup, InputGroupAddon } from '@wds/input-group';
import { Typography } from '@wds/typography';
import { Grid } from '@wds/grid';
import { List } from '@wds/list';
import { Link } from '@wds/link';
import { Button } from '@wds/button';

import PropTypes from 'prop-types';
import STRINGS from '../../../constants';

const Search = (props) => {
	const { show, setShow } = props;
	const [searchTerm, setSearchTerm] = useState('');
	const [facets, setFacets] = useState(['user', 'submission', 'award']);
	const [results, setResults] = useState([]);
	const debounceTimeout = useRef(null);
	const searchRef = useRef(null);

	const SearchBoxStyle = {
		background: '#fff',
		boxShadow: '0 2px 8px 0 rgba(0,0,0,.14)',
		fontSize: '14px',
		position: 'fixed',
		right: '200px',
		top: '48px',
		width: '550px',
		zIndex: '1030',
		cursor: 'auto',
	};

	const facetOptions = [
		{
			label: 'Users',
			value: 'user',
			icon: <i className='nc-icon-mini users_single-01' />,
			ariaLabel: 'Users Facet',
		},
		{
			label: 'Applications',
			value: 'submission',
			icon: <i className='nc-icon-mini files_single-content-03' />,
			ariaLabel: 'Applications Facet',
		},
		{
			label: 'Programs',
			value: 'award',
			icon: <i className='nc-icon-mini location_bookmark' />,
			ariaLabel: 'Programs Facet',
		},
	];

	// eslint-disable-next-line no-shadow
	const fetchResults = (searchTerm) => {
		let url = reverse('admin-search');
		url += `?q=${searchTerm}&facets=${JSON.stringify(facets)}`;
		fetch(url, {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
		})
			.then((response) => response.json())
			.then((data) => {
				setResults(data.quick_results);
			})
			.catch((error) => {
				setResults([]);
				console.error('Error:', error);
			});
	};

	const handleUpdateFacets = (value) => {
		if (facets.includes(value)) {
			setFacets(facets.filter((f) => f !== value));
		} else {
			setFacets([...facets, value]);
		}
	};

	useEffect(() => {
		if (searchTerm) {
			fetchResults(searchTerm);
		}
	}, [facets]);

	useEffect(() => {
		if (!searchTerm) {
			setResults([]);
		}
	}, [searchTerm]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (searchRef.current && !searchRef.current.contains(event.target)) {
				setShow(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [setShow]);

	const handleSearch = (e) => {
		const { value } = e.target;
		setSearchTerm(value);

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}
		debounceTimeout.current = setTimeout(() => {
			if (value) {
				fetchResults(value);
			}
		}, 300);
	};

	function getIcon(typeKey) {
		if (typeKey === 'user') {
			return <i className='nc-icon-mini users_single-01' />;
		}
		if (typeKey === 'submission') {
			return <i className='nc-icon-mini files_single-content-03' />;
		}
		if (typeKey === 'award') {
			return <i className='nc-icon-mini location_bookmark' />;
		}
		return null;
	}

	return (
		<div id='global-search' style={SearchBoxStyle} ref={searchRef} area-label='search-tool' role='region'>
			<Box width={550} display={show ? undefined : 'none'} p={4} area-label='search-tool'>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='sectionTitleSm'>{STRINGS.labels.search}</Typography>
					<Box
						display='flex'
						justifyContent='space-around'
						gap={2}
						alignContent='center'
						alignItems='center'
						onClick={() => setShow(false)}
					>
						<IconX color='warning' />
						<Typography variant='body' color='warning' weight='medium'>Close</Typography>
					</Box>
				</Box>
				<Box mt={2}>
					<InputGroup stretched>
						<InputGroupAddon>
							<IconSearch />
						</InputGroupAddon>
						<Input
							value={searchTerm}
							placeholder={STRINGS.searchPlaceholder}
							size='lg'
							onChange={handleSearch}
							stretched
						/>
						<InputGroupAddon>
							<Button
								variant='text'
								label='Clear'
								onClick={() => setSearchTerm('')}
								aria-label={STRINGS.cancel}
							>
								<IconX />
							</Button>
						</InputGroupAddon>
					</InputGroup>
				</Box>
				{searchTerm && (
					<Box mt={4} style={{ borderTop: '1px solid #d0d2d3' }}>
						<Grid noGutters noBleeds>
							<Grid.Item xs={4}>
								<Box px={0} py={4}>
									<Typography variant='cardTitle' weight='medium'>Show only</Typography>
									<List interactive dense noRules>
										{facetOptions.map((option) => (
											<List.Item
												key={`${option.value}-${facets.includes(option.value)}`}
												marked={facets.includes(option.value)}
												onClick={() => handleUpdateFacets(option.value)}
												aria-label={option.ariaLabel}
											>
												<Typography variant='bodySm'>
													{option.icon}
													{' '}
													{option.label}
												</Typography>
											</List.Item>
										))}
									</List>
								</Box>
							</Grid.Item>
							<Grid.Item>
								<Box
									px={4}
									py={4}
									style={{
										borderLeft: '1px solid #d0d2d3',
										minHeight: 200,
										maxHeight: 450,
										overflowY: 'auto',
									}}
								>
									<Typography variant='cardTitle' weight='medium'>
										{`${results.length} results`}
									</Typography>
									<List noRules>
										{results.map((result) => (
											<List.Item
												style={{ cursor: 'pointer' }}
												key={result.url}
												aria-label={`${STRINGS.labels.searchResultItem} ${result.label}`}
											>
												<Link href={result.url} underline={false}>
													<Typography variant='bodySm'>
														<Box display='flex' alignItems='baseline' gap={4}>
															{getIcon(result.type_key)}
															{' '}
															{result.label}
														</Box>
													</Typography>
												</Link>
											</List.Item>
										))}
									</List>
								</Box>
							</Grid.Item>
						</Grid>
					</Box>
				)}
			</Box>
		</div>
	);
};

Search.propTypes = {
	show: PropTypes.bool.isRequired,
	setShow: PropTypes.func.isRequired,
};

export default Search;
